/**
 * Display formatters shared by web screens. BuildBee is a Canadian product: without a workspace
 * setting, money is Canadian dollars and dates are formatted for en-CA. Screens pass the
 * workspace currency and locale when they have them.
 */
export const DEFAULT_MONEY_CURRENCY = 'CAD';
export const DEFAULT_DISPLAY_LOCALE = 'en-CA';

export function formatMoney(cents: number | null | undefined, currency: string = DEFAULT_MONEY_CURRENCY, locale: string = DEFAULT_DISPLAY_LOCALE): string {
  if (cents == null) return '—';
  return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(cents / 100);
}

export function formatDate(value: Date | string | null | undefined, locale: string = DEFAULT_DISPLAY_LOCALE, opts?: Intl.DateTimeFormatOptions): string {
  if (!value) return '—';
  const d = typeof value === 'string' ? new Date(value.length === 10 ? `${value}T00:00:00` : value) : value;
  if (Number.isNaN(d.getTime())) return '—';
  return new Intl.DateTimeFormat(locale, opts ?? { year: 'numeric', month: 'short', day: 'numeric' }).format(d);
}

export function formatDateTime(value: Date | string | null | undefined, locale: string = DEFAULT_DISPLAY_LOCALE): string {
  return formatDate(value, locale, { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

export function formatRelative(value: Date | string | null | undefined, now = new Date()): string {
  if (!value) return '—';
  const d = typeof value === 'string' ? new Date(value) : value;
  const diff = (d.getTime() - now.getTime()) / 1000;
  const abs = Math.abs(diff);
  const rtf = new Intl.RelativeTimeFormat('en', { numeric: 'auto' });
  if (abs < 60) return rtf.format(Math.round(diff), 'second');
  if (abs < 3600) return rtf.format(Math.round(diff / 60), 'minute');
  if (abs < 86_400) return rtf.format(Math.round(diff / 3600), 'hour');
  if (abs < 30 * 86_400) return rtf.format(Math.round(diff / 86_400), 'day');
  return formatDate(d);
}

export function formatBps(bps: number | null | undefined): string {
  if (bps == null) return '—';
  return `${(bps / 100).toFixed(bps % 100 === 0 ? 0 : 2)}%`;
}

export function titleCase(value: string): string {
  return value.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}
